import { useContext, useState } from 'react';
import { Box, Button, styled, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dateFormat from 'date-fns-tz/format';
import { useTranslation } from 'react-i18next';

import { ClassSessionInfo, ClassStudentGroup } from '../../context/types';
import Loadable from '../common/loadable';
import { EditSessionDialog, EditSessionDialogProps } from './EditSessionDialog';
import { NoRecords } from './SharedComponents';
import { ClassContext } from '../../context/ClassContext';
import { getGroupsForSession } from './util';

interface SessionsTableProps {
    id: string;
    role?: string;
    userGroups: ClassStudentGroup[];
    classGroups: ClassStudentGroup[];
    sessions: ClassSessionInfo[] | undefined;
    className: string;
    isInstructor: boolean;
    isGuest: boolean;
    showingPastSessionsOnly?: boolean;
    showSections: boolean;
    onClickLaunch?: () => void;
    onClickSchedule?: () => void;
}

const ActionsBox = styled(Box)`
    display: flex;
    flex-direction: row;

    & > * {
        margin-right: 6px;
    }
`;

const TableCellTime = styled(TableCell)`
    width: auto;
    max-width: 200px;
`;

const TableCellTopic = styled(TableCell)`
    width: auto;
    min-width: 100px;
    max-width: 800px;
`;

const TableCellLabel = styled(TableCell)`
    min-width: 100px;
    max-width: 200px;
    width: auto;
`;

const TableCellSections = styled(TableCell)`
    min-width: 100px;
    width: auto;
`;

const TableCellAction = styled(TableCell)`
    width: auto;
`;

const TableCellNoWrap = styled(TableCell)`
    white-space: nowrap;
`;

export function sessionOpened(session: ClassSessionInfo): boolean {
    const now = new Date().getTime();
    return (
        new Date(Number(session.start_time) * 1000).getTime() <= now &&
        new Date(Number(session.end_time) * 1000).getTime() >= now
    );
}

const buildResult = (t, id, classHasGroups: boolean, label?: string, section?: string) => (
    <>
        <TableCell key={`label-${id}`}>{label && <Typography>{label}</Typography>}</TableCell>
        {classHasGroups && <TableCell key={`section-${id}`}>{section && <Typography>{section}</Typography>}</TableCell>}
    </>
);

interface ConditionalTableCellsProps {
    t: any;
    id: string;
    session: ClassSessionInfo;
    classGroups: ClassStudentGroup[];
    myGroups: ClassStudentGroup[];
    showSections: boolean;
    role?: string;
}

const ConditionalTableCells = ({
    t,
    id,
    session,
    classGroups,
    myGroups,
    showSections,
    role
}: ConditionalTableCellsProps) => {
    if (!session.class_session_group) {
        // empty result
        return buildResult(t, id, showSections);
    }

    const { label, selectedGroups: sessionAssignedGroupIds = [] } = session.class_session_group.config;

    if (!showSections) return buildResult(t, id, false, label);

    if (sessionAssignedGroupIds.includes('all')) return buildResult(t, id, true, label, t('class:all-sections'));

    const groups = getGroupsForSession(session, classGroups, myGroups, role);
    const selectedGroupLabels: string[] = groups.map((g) => g.label);

    return buildResult(t, id, true, label, selectedGroupLabels.join(', '));
};

export const SessionsTable = (props: SessionsTableProps) => {
    const {
        id,
        role,
        userGroups,
        classGroups,
        sessions,
        isInstructor,
        isGuest,
        showingPastSessionsOnly,
        showSections,
        onClickSchedule,
        onClickLaunch
    } = props;
    const { t } = useTranslation(['class']);
    const { isLoading } = useContext(ClassContext);

    const classHasGroups: boolean = !!classGroups?.length;
    const initEditSessionDialogProps: EditSessionDialogProps = {
        open: false,
        session: undefined,
        setProps: undefined
    };
    const [editSessionDialogProps, setEditSessionDialogProps] =
        useState<EditSessionDialogProps>(initEditSessionDialogProps);

    const onClickEdit = (session: ClassSessionInfo) => {
        setEditSessionDialogProps({ open: true, session, setProps: setEditSessionDialogProps });
    };

    return (
        <Loadable fullScreen={true} isLoading={isLoading}>
            <EditSessionDialog {...editSessionDialogProps} />

            <Table>
                <TableHead>
                    <TableRow key={`header-${id}`}>
                        <TableCellTime>{t('class:time')}</TableCellTime>
                        <TableCellTopic>{t('class:topic')}</TableCellTopic>
                        <TableCellLabel>{t('class:label')}</TableCellLabel>
                        {classHasGroups && showSections && <TableCellSections>{t('class:sections')}</TableCellSections>}
                        <TableCellAction>{t('class:actions')}</TableCellAction>
                    </TableRow>
                    {sessions &&
                        sessions.map((session) => (
                            <TableRow key={`${id}-${session.class_session_uuid}`}>
                                <TableCellNoWrap key={`date-${id}-${session.class_session_uuid}`}>
                                    {dateFormat(new Date(Number(session.start_time) * 1000), 'PPPP')}
                                    <br />
                                    {dateFormat(new Date(Number(session.start_time) * 1000), 'p')} -{' '}
                                    {dateFormat(new Date(Number(session.end_time) * 1000), 'p')}
                                </TableCellNoWrap>
                                <TableCell key={`topic-${id}-${session.class_session_uuid}`}>
                                    {session.class_session_topic}
                                </TableCell>
                                <ConditionalTableCells
                                    {...{
                                        t,
                                        id: `${id}-${session.class_session_uuid}`,
                                        session,
                                        classGroups,
                                        myGroups: userGroups,
                                        showSections: showSections && classHasGroups,
                                        role
                                    }}
                                ></ConditionalTableCells>
                                <TableCell key={`action-${id}-${session.class_session_uuid}`}>
                                    <ActionsBox>
                                        {isInstructor && (
                                            <Button
                                                size="medium"
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => {
                                                    onClickEdit(session);
                                                }}
                                            >
                                                {t('class:edit')}
                                            </Button>
                                        )}
                                        {!isGuest && sessionOpened(session) && (
                                            <Button
                                                size="medium"
                                                color="primary"
                                                variant="contained"
                                                onClick={onClickLaunch}
                                            >
                                                {t('class:launch')}
                                            </Button>
                                        )}
                                    </ActionsBox>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableHead>
            </Table>
            {!sessions?.length && (
                <NoRecords
                    message={
                        showingPastSessionsOnly ? t('class:no-past-sessions-found') : t('class:no-sessions-scheduled')
                    }
                >
                    {isInstructor && !showingPastSessionsOnly && (
                        <Button
                            sx={{ mt: 1 }}
                            size="medium"
                            color="primary"
                            variant="contained"
                            onClick={onClickSchedule}
                        >
                            {t('class:schedule-session')}
                        </Button>
                    )}
                </NoRecords>
            )}
        </Loadable>
    );
};
